import { ReactNode } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface CursorContextMenuPosition {
  /** Viewport coordinates — pass `event.clientX` / `event.clientY`. */
  x: number;
  y: number;
}

export interface CursorContextMenuItem {
  /** Stable identity for the item (React key). */
  key: string;
  label: ReactNode;
  /** Optional leading icon; rendered with a small right margin. */
  icon?: ReactNode;
  onSelect: () => void;
  /** Renders the item in the destructive tone (e.g. delete actions). */
  destructive?: boolean;
  disabled?: boolean;
}

interface CursorContextMenuProps {
  /** Where to anchor the menu; `null` keeps it closed/unmounted. */
  position: CursorContextMenuPosition | null;
  items: CursorContextMenuItem[];
  onClose: () => void;
}

/**
 * Custom right-click menu anchored at the cursor. A Radix dropdown attached to
 * an invisible fixed-position trigger — useful when the right-clickable
 * elements are rendered internally by another component (e.g. table rows), so
 * a wrapper-based ContextMenu trigger is not an option.
 *
 * Usage: keep `{ x, y, ...payload }` state in the parent, set it from an
 * `onContextMenu` handler (call `event.preventDefault()` there), and clear it
 * in `onClose`.
 */
export const CursorContextMenu = ({ position, items, onClose }: CursorContextMenuProps) => {
  if (!position) return null;

  return (
    <DropdownMenu open onOpenChange={isOpen => !isOpen && onClose()}>
      <DropdownMenuTrigger asChild>
        <span aria-hidden className="fixed h-0 w-0" style={{ left: position.x, top: position.y }} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="bottom">
        {items.map(item => (
          <DropdownMenuItem
            key={item.key}
            onClick={item.onSelect}
            disabled={item.disabled}
            className={cn(item.destructive && 'text-destructive focus:text-destructive')}
          >
            {item.icon && <span className="mr-2 flex shrink-0 items-center">{item.icon}</span>}
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
